import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import AsyncSelect from 'react-select/async';
import { audiencesApi } from '../../api/audiences';
import AddIcon from '../../assets/icons/list_plus_raw.svg';
import Modal from '../../components/base/Modal';
import ModalFooter from '../../components/base/Modal/ModalFooter';
import ModalTitle from '../../components/base/Modal/ModalTitle';
import {
  showErrorMessage,
  showSuccessMessage,
} from '../../components/base/Notifications';
import { debounce } from '../../utils/debounce';
import {
  MultiValueLabel,
  MultiValueRemove,
} from '../AddSegment/components/labels';
import { placeholderStyle } from '../AddSegment/Condition/Condition.constants';
import styles from './Contacts.module.scss';

const AddToListModal = ({ contacts, closeModal, onAdd }) => {
  const [selectedLists, setSelectedLists] = useState([]);
  const [inputTextValue, setInputTextValue] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [finalStatus, setFinalStatus] = useState({ message: '', type: '' });
  const selectRef = useRef(null);

  const dispatch = useDispatch();
  const [updateCustomList] = audiencesApi.useUpdateCustomListMutation();

  const loadOptions = useCallback(
    async (inputValue) => {
      try {
        const listsApiResult = await dispatch(
          audiencesApi.endpoints.getListOptions.initiate(
            {
              search: inputValue,
            },
            {
              forceRefetch: true,
            }
          )
        ).unwrap();

        const lists = listsApiResult.map((item) => ({
          value: item.id,
          label: item.name,
        }));

        const sortedLists = lists.sort((a, b) =>
          a.label.localeCompare(b.label)
        );
        return sortedLists;
      } catch (error) {
        return [];
      }
    },
    [dispatch]
  );

  const debouncedLoadOptions = useMemo(
    () => debounce(loadOptions, 1000),
    [loadOptions]
  );

  const [createCustomListAddresses, createCustomListAddressesResult] =
    audiencesApi.useSubmitCustomListMutation();

  const handleCreateList = useCallback(
    async (name) => {
      const trimmedName = name.trim();
      if (!trimmedName) {
        return;
      }

      try {
        const data = await createCustomListAddresses({
          name: trimmedName,
          addresses: [],
        }).unwrap();

        if (data?.id && data?.name) {
          const newOption = { value: data.id, label: data.name };
          setSelectedLists((prev) => {
            const alreadySelected = prev.some(
              (item) => item.value === newOption.value
            );
            return alreadySelected ? prev : [...prev, newOption];
          });
        }

        showSuccessMessage('List was successfully created');
        setInputTextValue('');
        selectRef.current?.blur();
      } catch (error) {
        if (error?.status === 400 && error?.data?.name?.length) {
          showErrorMessage('A list with this name already exists.');
        } else {
          showErrorMessage(
            error?.data?.message || 'An error occurred while creating the list.'
          );
        }
      }
    },
    [createCustomListAddresses]
  );

  const handleConfirm = async () => {
    if (contacts.length === 0 || selectedLists.length === 0) {
      setFinalStatus({
        message: 'Please select both contacts and lists.',
        type: 'error',
      });
      return;
    }

    setIsUpdating(true);
    setFinalStatus({ message: '', type: '' });

    const addresses = contacts.map((contact) => contact.address);

    const updatePromises = selectedLists.map((list) =>
      updateCustomList({
        id: list.value,
        body: { add: addresses },
      }).unwrap()
    );

    try {
      await Promise.all(updatePromises);

      const contactText = addresses.length === 1 ? 'contact' : 'contacts';
      const listText = selectedLists.length === 1 ? 'list' : 'lists';
      setFinalStatus({
        message: `Successfully added ${addresses.length} ${contactText} to ${selectedLists.length} ${listText}.`,
        type: 'success',
      });
    } catch (error) {
      setFinalStatus({
        message:
          error.data?.message || 'An error occurred while updating the lists.',
        type: 'error',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    if (finalStatus.type === 'success') {
      showSuccessMessage(finalStatus.message);
      const addresses = contacts.map((contact) => contact.address);
      onAdd(addresses, selectedLists);
      closeModal();
    } else if (finalStatus.type === 'error') {
      showErrorMessage(finalStatus.message);
    }
  }, [closeModal, contacts, finalStatus, onAdd, selectedLists]);

  return (
    <Modal
      onCancel={closeModal}
      loading={isUpdating}
      className={styles.add_to_list_modal}
    >
      <ModalTitle title="Add to List" />

      <AsyncSelect
        ref={selectRef}
        styles={{
          ...placeholderStyle,
          menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
          }),
        }}
        maxMenuHeight={194}
        components={{
          // eslint-disable-next-line react/no-unstable-nested-components
          MultiValueRemove: (props) => (
            <MultiValueRemove props={props} show="label" />
          ),
          // eslint-disable-next-line react/no-unstable-nested-components
          MultiValueLabel: (props) => (
            <MultiValueLabel props={props} show="label" />
          ),
        }}
        className="w-100"
        value={selectedLists}
        loadOptions={debouncedLoadOptions}
        isSearchable
        onChange={(newValue) => {
          setSelectedLists(newValue || []);
        }}
        defaultOptions
        cacheOptions
        isShowValue
        isMulti
        menuPortalTarget={document.body}
        inputValue={inputTextValue}
        onInputChange={(newValue, { action }) => {
          if (action === 'input-change') setInputTextValue(newValue);
        }}
        noOptionsMessage={({ inputValue }) => {
          const trimmedValue = inputValue?.trim() || '';

          const isDisabled =
            !trimmedValue || createCustomListAddressesResult.isLoading;

          return (
            <button
              type="button"
              className={styles.no_options_button}
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
                if (isDisabled) {
                  return;
                }
                handleCreateList(trimmedValue);
              }}
              disabled={isDisabled}
            >
              {createCustomListAddressesResult.isLoading
                ? 'Creating list...'
                : trimmedValue
                  ? `Create "${trimmedValue}"`
                  : 'Type a name to create a list'}
            </button>
          );
        }}
      />

      <ModalFooter>
        <button
          type="button"
          className="outline-button border border-1"
          onClick={closeModal}
        >
          Cancel
        </button>
        <button
          type="button"
          className="regular-button"
          onClick={handleConfirm}
          disabled={selectedLists.length === 0}
        >
          Add
        </button>
      </ModalFooter>
    </Modal>
  );
};

const AddToListButton = ({ contacts, onAdd }) => {
  const [showAddToListModal, setShowAddToListModal] = useState(false);

  return (
    <div className={styles.add_to_list}>
      <button
        className={styles.add_to_list_button}
        type="button"
        onClick={() => setShowAddToListModal(true)}
      >
        <AddIcon />
      </button>
      {showAddToListModal && (
        <AddToListModal
          contacts={contacts}
          closeModal={() => setShowAddToListModal(false)}
          onAdd={onAdd}
        />
      )}
    </div>
  );
};

export default AddToListButton;
